import { useState, useEffect } from 'react';

const DISMISS_KEY = 'hs_install_banner_dismissed_at';
const DISMISS_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isDismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) < DISMISS_EXPIRY_MS;
  } catch {
    return false;
  }
}

function detectIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIosDevice = /iPhone|iPad|iPod/.test(ua);
  const isStandalone = (window.navigator as any).standalone === true;
  return isIosDevice && !isStandalone;
}

export function useInstallBanner() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState<boolean>(isDismissedRecently);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true);

  const isIos = detectIos();

  useEffect(() => {
    if (isStandalone || isDismissed) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone, isDismissed]);

  const install = async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === 'accepted') {
      setPromptEvent(null);
      setIsDismissed(true);
    }
  };

  const dismiss = () => {
    setPromptEvent(null);
    setIsDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  // Show on Android/Windows when browser fires beforeinstallprompt,
  // or on iOS Safari (which never fires that event)
  const showBanner = !isStandalone && !isDismissed && (promptEvent !== null || isIos);

  return { showBanner, isIos, install, dismiss, promptAvailable: promptEvent !== null };
}
